import { debounce } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { nextTick } from 'vue';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { isEmptyFrameError } from '@/services/api/client';
import { errorLogSummary } from '@/services/api/error-parser';
import { httpStatusOf } from '@/services/api/http';
import { kioskErrorProperties } from '@/services/telemetry/kiosk-error';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';
import { sentryService } from '@/services/telemetry/sentry';
import {
  portraitAspectRatioForOrientation,
  resolvePortraitOrientation,
} from '@/shared/lib/portrait-orientation';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import extractObjectDataProperties from '@/shared/utils/extract-object-data-properties';
import { fetchAuthenticatedImage } from '@/shared/utils/fetch-authenticated-image';
import {
  groupKey,
  orderPeopleLeftToRight,
  splitSinglesOverFaces,
} from './group-portrait-subjects';
import { kioskDesign } from './KioskDesign';
import { printing } from './Printing';
import { createRevealPacer } from './RevealPacer';

/**
 * Concurrency limiter for portrait generation requests.
 *
 * Controls how many generation API calls can be in-flight simultaneously.
 * Configured via `kiosk.parallel_generations` (default 1 = sequential).
 *
 * Usage:
 *   const sem = new Semaphore(3);
 *   await sem.run(() => generateSingle(...));  // at most 3 concurrent
 */
const PROXY_ERROR_STATUSES = new Set([502, 503, 504]);

// The shape of a group portrait when the 202 gives no output_size: the
// workflow group portraits are 2:3 (1664x2496).
export const GROUP_PORTRAIT_DEFAULT_RATIO = 2 / 3;

class Semaphore {
  constructor(max) {
    this._max = Math.max(1, max);
    this._active = 0;
    this._waiting = [];
  }

  acquire() {
    if (this._active < this._max) {
      this._active++;
      return Promise.resolve();
    }
    return new Promise((resolve) => this._waiting.push(resolve));
  }

  release() {
    if (this._waiting.length > 0) {
      // Hand the slot directly to the next waiter (no decrement/increment)
      this._waiting.shift()();
    } else {
      this._active--;
    }
  }

  async run(fn) {
    await this.acquire();
    try {
      return await fn();
    } finally {
      this.release();
    }
  }

  /** Returns a snapshot of current semaphore state for diagnostics. */
  getState() {
    return {
      active: this._active,
      waiting: this._waiting.length,
      max: this._max,
    };
  }
}

/**
 * Preserve full UUIDs for Sentry correlation.
 * These are internal operational identifiers used to join generated images,
 * face crops, sessions, and backend logs. Do not reintroduce truncation here.
 * @param {string} uuid - Full UUID
 * @returns {string} - Full UUID
 */
function sentryUuid(uuid) {
  return uuid || 'unknown';
}

/**
 * These functions give the Sentry identifiers of a portrait. withSpan stays
 * here. If sentryService.withSpan throws null or undefined before the callback
 * starts, tracing.withSpan does not run the callback. This wrapper runs it.
 */
const safeSentry = {
  /**
   * Categorize error for Sentry tagging to enable filtering and analysis.
   * @param {Error|Object} error - The error to categorize
   * @returns {string} - Error category for tagging
   */
  categorizeError(error) {
    if (!error) return 'unknown';
    const message = error.message?.toLowerCase() || '';
    const name = error.name?.toLowerCase() || '';

    if (name === 'aborterror' || message.includes('abort')) return 'cancelled';
    if (name === 'typeerror' && message.includes('fetch'))
      return 'network_error';
    if (message.includes('timeout') || message.includes('timed out'))
      return 'timeout';
    if (message.includes('rate limit') || message.includes('429'))
      return 'rate_limited';
    if (message.includes('quota') || message.includes('limit exceeded'))
      return 'quota_exceeded';
    if (
      message.includes('nsfw') ||
      message.includes('content') ||
      message.includes('moderation')
    )
      return 'content_moderation';
    if (message.includes('validation') || message.includes('invalid'))
      return 'validation_error';
    if (error.status >= 500 || message.includes('server'))
      return 'server_error';
    if (error.status >= 400) return 'client_error';
    return 'unknown';
  },

  /**
   * Wrap async function with Sentry span, with fallback if Sentry fails.
   * If span creation fails, the function still executes without tracking.
   * @param {string} name - Span name
   * @param {string} op - Operation type
   * @param {Function} fn - Async function to wrap
   * @param {Object} attributes - Span attributes (will be sanitized)
   * @returns {Promise} - Result of the function
   */
  async withSpan(name, op, fn, attributes = {}) {
    // Track whether fn was already invoked inside the span.
    // If fn ran and threw, the error propagates through Sentry's span handling
    // into our catch. Re-executing fn() would cause duplicate API calls
    // (e.g., generating a portrait twice, or calling _requireSessionId()
    // after session reset when the first call was aborted).
    let fnExecuted = false;
    const trackedFn = async () => {
      fnExecuted = true;
      return fn();
    };
    try {
      return await sentryService.withSpan(name, op, trackedFn, attributes);
    } catch (error) {
      if (fnExecuted) {
        // fn() already ran and threw — rethrow, don't re-execute
        throw error;
      }
      // Span creation failed before fn ran — execute without tracking
      console.warn(
        '[Sentry] Span creation failed, executing without tracking:',
        error.message
      );
      return await fn();
    }
  },

  /**
   * Sanitize face UUIDs array for logging.
   * @param {string[]} uuids - Array of face UUIDs
   * @returns {string} - Truncated, comma-separated string
   */
  sanitizeFaceUuids(uuids) {
    if (!Array.isArray(uuids)) {
      return 'none';
    }
    return uuids.map((u) => sentryUuid(u)).join(',');
  },
};

export class PortraitGeneration extends PubSub {
  EVENTS = {
    GENERATED_SINGLE: 'GENERATED_SINGLE',
    GENERATED_AND_PRELOADED_SINGLE: 'GENERATED_AND_PRELOADED_SINGLE',
    GENERATED_AND_PRELOADED_ALL: 'GENERATED_AND_PRELOADED_ALL',
    GROUP_GENERATED_AND_PRELOADED: 'GROUP_GENERATED_AND_PRELOADED',
  };

  STATUSES = {
    NEVER_GENERATED: 'NEVER_GENERATED',
    GENERATING_NOW: 'GENERATING_NOW',
    GENERATED_NOT_PRELOADED: 'GENERATED_NOT_PRELOADED',
    GENERATED_AND_PRELOADED: 'GENERATED_AND_PRELOADED',
    ERROR: 'ERROR',
  };

  ATTEMPTS_LIMIT = 4; // Initial generation + 3 more attempts

  // Submit→poll budget: generation runs 30-120s server-side; the poll
  // (not the HTTP request) owns the wait, mirroring Printing.js. Fallback
  // only — when the 202 carries budget_s, the server's budget wins.
  GENERATION_POLL_TIMEOUT_MS = 3 * 60 * 1000;
  // How long a job may sit queued before the worker picks it up, on top of
  // the budget the worker then spends on it.
  GENERATION_QUEUE_WAIT_MAX_MS = 2 * 60 * 1000;
  GENERATION_POLL_INITIAL_DELAY_MS = 1000;
  GENERATION_POLL_MAX_DELAY_MS = 3000;
  // Fallback only: the 202 of a group submit carries budget_s, and the
  // kiosk waits for that budget, as for a single portrait.
  GROUP_PORTRAIT_POLL_TIMEOUT_MS = 10 * 60 * 1000;
  // The visitor retries of a failed tile stop here, for the singles and the
  // group alike. The automatic retry counts as the first.
  MAX_RETRY_COUNT = 3;

  // The face selections, by style, that no group config takes: they get no
  // group tile and no second submit. A different selection gets a new submit.
  _skippedGroupSelections = new Set();

  @reactive
  portraits = [];

  @reactive
  groupPortraits = [];

  @reactive
  attemptsCount = 0;

  @reactive
  isGenerating = false;

  // The promise of the batch that launch() runs, while isGenerating is set.
  _activeLaunch = null;

  @reactive
  groupGeneratingCount = 0;

  get isGroupGenerating() {
    return this.groupGeneratingCount > 0;
  }

  // Paces how fast finished portraits appear in the gallery. Generation stays
  // fully parallel — only the reveal is rate-limited, so raising
  // `parallel_generations` speeds up results without the gallery snapping
  // several tiles in at once.
  _revealPacer = createRevealPacer({
    onError: (error) =>
      console.error('[Portrait] Paced reveal failed:', error?.message || error),
  });

  // Track what's been generated per unique session/style/faces combination
  generationState = new Map();

  // AbortController for cancelling pending staggered group generation timeouts
  _groupGenerationController = null;

  // Monotonically increasing epoch for group portrait generation batches.
  // When ensureInitialGroupPortraits() starts a new batch, it increments the epoch.
  // Each in-flight group generation captures the epoch at launch time and compares
  // it before applying results. If the epoch has advanced (new batch started),
  // the result is from a stale batch and should be discarded.
  // This closes the race where the AbortController only cancels delay timers
  // but not in-flight HTTP requests from the previous batch.
  _groupGenerationEpoch = 0;

  // Session ID captured when generation starts. Compared against store.sessionId
  // in async result handlers to prevent stale session A results from polluting session B.
  // Set to null on reset() to invalidate all in-flight operations.
  _generationSessionId = null;

  // Generation metrics tracking for Sentry
  _metrics = {
    totalGenerated: 0,
    totalFailed: 0,
    totalRetries: 0,
    groupGenerated: 0,
    groupFailed: 0,
  };

  get attemptsLeft() {
    // Limit to 2 additional attempts after initial generation
    const maxAdditionalAttempts = 2;
    const additionalAttemptsSoFar = Math.max(0, this.attemptsCount - 1); // -1 for initial generation
    return Math.max(0, maxAdditionalAttempts - additionalAttemptsSoFar);
  }

  /**
   * Group consecutive portraits by style for parallel dispatch.
   * E.g. [A, A, B, B, A, A] → [[A, A], [B, B], [A, A]]
   * Each group can be fired in parallel; groups run sequentially
   * to preserve the style rotation order in the gallery.
   */
  _groupByStyle(portraitsToGenerate) {
    const groups = [];
    let current = [];
    let currentStyle = null;
    for (const p of portraitsToGenerate) {
      if (p.styleId !== currentStyle) {
        if (current.length) groups.push(current);
        current = [p];
        currentStyle = p.styleId;
      } else {
        current.push(p);
      }
    }
    if (current.length) groups.push(current);
    return groups;
  }

  /**
   * Detects if an error is from an intentional request abort (e.g., user navigation).
   * Abort errors should not be treated as generation failures.
   *
   * Http._handleFetchError wraps aborts as ParsedError with:
   *   - error.context.isRequestManuallyAborted = true
   *   - error.cause = original DOMException (name: 'AbortError')
   *
   * We check each path because errors may arrive raw (DOMException from fetch)
   * or wrapped (ParsedError from Http layer).
   *
   * @param {Error} error - The error to check
   * @returns {boolean} - True if the error is from an abort
   */
  isAbortError(error) {
    // Direct AbortError (raw fetch abort)
    if (error?.name === 'AbortError') return true;

    // ParsedError from Http._handleFetchError sets context.isRequestManuallyAborted
    if (error?.context?.isRequestManuallyAborted === true) return true;

    // Chained cause may be a DOMException AbortError
    if (error?.cause?.name === 'AbortError') return true;

    // The session ended and its cancel failed this job. A poll asleep between
    // requests misses the local abort and reads that status instead.
    if (error?.code === 'JOB_CANCELED') return true;

    return false;
  }

  /**
   * Check if the current generation session is still valid.
   * Returns false if reset() was called or if the store session ID
   * no longer matches the generation session ID (stale result from previous session).
   * @returns {boolean}
   */
  _isSessionValid() {
    return (
      this._generationSessionId !== null &&
      this._generationSessionId === store.sessionId
    );
  }

  constructor() {
    super();
    exposeToGlobalScope(this, 'PortraitGeneration');
  }

  /**
   * Updates Sentry context with current generation progress and metrics.
   * Called after significant state changes for debugging context.
   */
  _updateSentryContext() {
    const totalAttempts =
      this._metrics.totalGenerated + this._metrics.totalFailed;
    sentryService.setContext('portrait_generation', {
      portraits_count: this.portraits.length,
      group_portraits_count: this.groupPortraits.length,
      generating: this.isGenerating,
      group_generating_count: this.groupGeneratingCount,
      attempts_count: this.attemptsCount,
      attempts_left: this.attemptsLeft,
      total_generated: this._metrics.totalGenerated,
      total_failed: this._metrics.totalFailed,
      total_retries: this._metrics.totalRetries,
      group_generated: this._metrics.groupGenerated,
      group_failed: this._metrics.groupFailed,
      success_rate:
        totalAttempts > 0
          ? `${((this._metrics.totalGenerated / totalAttempts) * 100).toFixed(1)}%`
          : 'N/A',
    });
  }

  /**
   * Debounced version of _updateSentryContext for non-critical progress updates.
   * Prevents excessive Sentry API calls during rapid state changes.
   * Use immediate _updateSentryContext() for critical states (errors, completion).
   */
  _updateSentryContextDebounced = debounce(() => {
    this._updateSentryContext();
  }, 500);

  // Generation state tracking methods
  getStateKey(type, faces) {
    // Create unique key for current state combining type, session, style, and faces
    // Throw if no session - this prevents silent failures and stale state tracking
    if (!store.sessionId) {
      throw new Error(
        '[PortraitGeneration] Cannot generate state key: no session'
      );
    }
    const sessionId = store.sessionId;
    const styleId = store.selectedStyle?.id || 'no-style';
    const faceIds = faces
      .map((f) => f.uuid)
      .sort()
      .join(',');
    return `${type}_${sessionId}_${styleId}_${faceIds}`;
  }

  hasGeneratedInitial(type, faces) {
    const key = this.getStateKey(type, faces);
    return this.generationState.has(key);
  }

  markAsGenerated(type, faces) {
    const key = this.getStateKey(type, faces);
    this.generationState.set(key, true);
  }

  resetGenerationState() {
    this.generationState.clear();
  }

  normalize(rawPortrait) {
    const {
      NEVER_GENERATED,
      GENERATING_NOW,
      GENERATED_NOT_PRELOADED,
      GENERATED_AND_PRELOADED,
      ERROR,
    } = this.STATUSES;
    return {
      // Generate temp UUID for tracking until backend provides real UUID
      // Always generate a UUID if none exists to ensure proper tracking
      uuid: rawPortrait.uuid || uuidv4(),
      status: rawPortrait.status || NEVER_GENERATED,
      faceUuid: rawPortrait.faceUuid,
      faceUuids: rawPortrait.faceUuids, // For group portraits
      styleId: rawPortrait.styleId,
      age_group: rawPortrait.age_group,
      gender: rawPortrait.gender,
      race: rawPortrait.race,
      priority: rawPortrait.priority || this.getPriority(),
      configUuid: rawPortrait.configUuid || rawPortrait.config_uuid || null,
      url: rawPortrait.url,
      promise: rawPortrait.promise,
      error: rawPortrait.error || null,
      _originalUrl: rawPortrait._originalUrl || null,
      isGroupPortrait: rawPortrait.isGroupPortrait,
      aspectRatio:
        rawPortrait.aspectRatio ||
        (rawPortrait.isGroupPortrait
          ? GROUP_PORTRAIT_DEFAULT_RATIO
          : portraitAspectRatioForOrientation(
              store.isVerticalKiosk
                ? resolvePortraitOrientation(kioskDesign.kiosk)
                : 'vertical'
            )),
      isRetrying: rawPortrait.isRetrying,
      retryCount: rawPortrait.retryCount || 0,
      _completedAt: rawPortrait._completedAt || 0,
      _pendingRequestId: rawPortrait._pendingRequestId || null,

      get isNeverGenerated() {
        return this.status === NEVER_GENERATED;
      },
      get isGenerating() {
        return this.status === GENERATING_NOW;
      },
      get isGenerated() {
        return (
          this.status === GENERATED_NOT_PRELOADED ||
          this.status === GENERATED_AND_PRELOADED
        );
      },
      get isPreloaded() {
        return this.status === GENERATED_AND_PRELOADED;
      },
      get isError() {
        return this.status === ERROR;
      },
    };
  }

  getByUuid(uuid) {
    // Check both regular portraits and group portraits
    return (
      this.portraits.find((portrait) => portrait.uuid === uuid) ||
      this.groupPortraits.find((portrait) => portrait.uuid === uuid)
    );
  }

  update(uuid, diff) {
    const portrait = this.getByUuid(uuid);
    if (!portrait) {
      console.error(
        '[Portrait] Cannot update: portrait not found with UUID:',
        uuid
      );
      return null;
    }
    const dataProps = extractObjectDataProperties(
      this.normalize({ ...portrait, ...diff })
    );
    return Object.assign(portrait, dataProps);
  }

  getPriority(isHigherPriority) {
    const { portrait_priority_1st, portrait_priority_default } = store.kiosk;
    if (isHigherPriority) {
      return portrait_priority_1st;
    }
    return portrait_priority_default || 'medium';
  }

  // The group scene follows the same table shape as the singles, one row
  // for now: the scene is what a multi-subject session came for, and every
  // extra one is a whole workflow run (about a minute on Seedream). The
  // attempt ceiling is the singles' one: "+" queues the singles first and
  // throws past the limit, so the group top-up never runs alone.
  getGroupPortraitCount(faceCount, options) {
    // A kiosk opts in: a missing or null flag means no group portrait.
    if (faceCount < 2 || store.kiosk?.multi_subject_enabled !== true) {
      return 0;
    }
    const counts = [
      // Two faces up to the MAX_FACES cap that FaceDetection applies. A
      // count outside the band matches no row and yields no group scene.
      {
        faceCount: [2, 3],
        initialPortraitCount: 1,
        additionalPortraitCount: 1,
      },
    ];
    const count = counts.find((option) => {
      const [from, to] = option.faceCount;
      return faceCount >= from && faceCount <= to;
    });
    if (!count) {
      return 0;
    }
    return options?.additional
      ? count.additionalPortraitCount
      : count.initialPortraitCount;
  }

  getPortraitCountPerFace(faceCount, options) {
    const defaultPortraitCount = 1;
    const isVerticalKiosk = store.isVerticalKiosk;

    // Vertical kiosk: generate 8 portraits for better 2x2 grid display
    const counts = isVerticalKiosk
      ? [
          { faceCount: 1, initialPortraitCount: 8, additionalPortraitCount: 4 },
          { faceCount: 2, initialPortraitCount: 4, additionalPortraitCount: 2 },
          { faceCount: 3, initialPortraitCount: 2, additionalPortraitCount: 2 },
        ]
      : [
          { faceCount: 1, initialPortraitCount: 6, additionalPortraitCount: 3 },
          { faceCount: 2, initialPortraitCount: 3, additionalPortraitCount: 2 },
          { faceCount: 3, initialPortraitCount: 2, additionalPortraitCount: 1 },
        ];

    const count = counts.find((option) => {
      const from = option.faceCount?.[0] || option.faceCount;
      const to = option.faceCount?.[1] || option.faceCount;
      return faceCount >= from && faceCount <= to;
    });

    if (count) {
      if (options?.additional && this.attemptsLeft === 0) {
        return 0; // No more additional portraits allowed
      }

      return options?.additional
        ? count.additionalPortraitCount
        : count.initialPortraitCount;
    }
    return defaultPortraitCount;
  }

  addToQueue(faces, options) {
    if (faces.length === 0) {
      useUiStore().showError('portrait_generation_faces_not_selected');
      throw new Error('portrait_generation_faces_not_selected');
    }
    if (!store.selectedStyle?.id) {
      useUiStore().showError('portrait_generation_style_not_selected');
      throw new Error('portrait_generation_style_not_selected');
    }
    if (options?.additional && this.attemptsLeft === 0) {
      useUiStore().showError('portrait_generation_over_limit');
      throw new Error('portrait_generation_over_limit');
    }

    // Capture current session ID for stale-result detection.
    // In-flight promise handlers compare this against store.sessionId
    // to prevent session A results from polluting session B.
    this._generationSessionId = store.sessionId;

    const portraits = [];
    const portraitCountPerFace = this.getPortraitCountPerFace(
      faces.length,
      options
    );
    // The initial group portraits take the places of singles, thus the
    // session keeps its count of portraits. "+" adds its group on top.
    const groupSlots = options?.additional
      ? 0
      : Math.max(0, options?.groupSlots ?? 0);
    const singlesPerFace = splitSinglesOverFaces(
      portraitCountPerFace * faces.length - groupSlots,
      faces.length
    );

    // Generate all images for each person sequentially (Person1: 1,2,3 then Person2: 1,2,3)
    // instead of alternating between people (Person1: 1, Person2: 1, Person3: 1, Person1: 2...)
    for (const [faceIndex, face] of faces.entries()) {
      for (
        let chunkIndex = 0;
        chunkIndex < (singlesPerFace[faceIndex] ?? 0);
        chunkIndex++
      ) {
        portraits.push(
          this._queuedSingle(face, portraits.length, chunkIndex === 0)
        );
      }
    }

    this.attemptsCount++;
    this.portraits = [...this.portraits, ...portraits];

    // Sentry breadcrumb for queue addition (safe - never throws)
    sentryService.addBreadcrumb(
      'portrait.queue',
      `Added ${portraits.length} portraits to queue`,
      {
        portrait_count: portraits.length,
        face_count: faces.length,
        attempt_number: this.attemptsCount,
        is_additional: options?.additional,
      }
    );
    this._updateSentryContextDebounced();

    return portraits;
  }

  // A single for `face`, not queued yet. `styleIndex` goes on with the style
  // rotation of the kiosk; the first single of each face gets the priority.
  _queuedSingle(face, styleIndex, isFirstOfFace) {
    const styleIds = kioskDesign.kiosk?.portrait_style_ids;
    const useStyleRotation = Array.isArray(styleIds) && styleIds.length > 0;
    // A visitor who picked a style on the selector gets only that style.
    const userSelectedStyle =
      kioskDesign.kiosk?.portrait_styles_enable_selector &&
      store.selectedStyle?.id;
    const styleId =
      !userSelectedStyle && useStyleRotation
        ? styleIds[styleIndex % styleIds.length]
        : store.selectedStyle.id;
    return this.normalize({
      age_group: face.age_group,
      gender: face.gender,
      race: face.race,
      faceUuid: face.uuid,
      styleId,
      priority: this.getPriority(isFirstOfFace),
    });
  }

  launch(options, isContinuing = false) {
    // Early return check before span - prevents concurrent generation batches
    if (!isContinuing && this.isGenerating) {
      return Promise.resolve();
    }
    const batch = this._launchBatch(options, isContinuing);
    if (!isContinuing) {
      this._activeLaunch = batch;
    }
    return batch;
  }

  // launch() drops a call while a batch runs, and that batch takes a new
  // portrait only up to its last check. Thus wait for the batch, then
  // launch what is left.
  async _launchWhenIdle(options) {
    let awaited = null;
    while (
      this.isGenerating &&
      this._activeLaunch &&
      this._activeLaunch !== awaited
    ) {
      awaited = this._activeLaunch;
      await awaited.catch(() => undefined);
    }
    if (
      this.isGenerating ||
      !this._isSessionValid() ||
      this.query({ neverGenerated: true }).length === 0
    ) {
      return [];
    }
    return this.launch(options);
  }

  async _launchBatch(options, isContinuing) {
    // CRITICAL: Set isGenerating BEFORE the async withSpan call to close the
    // race window. Without this, two launch() calls can both pass the guard
    // in launch() before either enters the withSpan callback.
    this.isGenerating = true;

    try {
      return await safeSentry.withSpan(
        'portrait.launch',
        'portrait.orchestration',
        async () => {
          const portraitsToGenerate = this.query({ neverGenerated: true });
          const maxConcurrent = kioskDesign.kiosk?.parallel_generations || 1;

          sentryService.addBreadcrumb(
            'portrait.launch',
            'Portrait generation batch launched',
            {
              portrait_count: portraitsToGenerate.length,
              is_continuing: isContinuing,
              mode: maxConcurrent > 1 ? 'parallel' : 'sequential',
              max_concurrent: maxConcurrent,
            }
          );

          sentryService.trackMetric(
            'portrait.batch_size',
            portraitsToGenerate.length,
            'none'
          );

          if (maxConcurrent > 1) {
            // Group consecutive same-style portraits and fire each group
            // in parallel. Groups run sequentially to preserve style rotation.
            const groups = this._groupByStyle(portraitsToGenerate);
            for (const group of groups) {
              if (!this._isSessionValid()) return;
              await this._launchParallel(group, options, maxConcurrent);
            }
          } else {
            await this._launchSequential(portraitsToGenerate, options);
          }

          // Don't continue if session ended
          if (!this._isSessionValid()) {
            return [];
          }

          // Check for more portraits to generate (recursive handling)
          if (this.query({ neverGenerated: true }).length > 0) {
            return this.launch(options, true);
          }

          sentryService.addBreadcrumb(
            'portrait.launch',
            'Portrait generation batch completed',
            {
              portrait_count: portraitsToGenerate.length,
            }
          );

          this.trigger(
            this.EVENTS.GENERATED_AND_PRELOADED_ALL,
            portraitsToGenerate
          );
          return portraitsToGenerate;
        },
        {
          'batch.is_continuing': isContinuing,
          'batch.mode': 'auto',
        }
      );
    } finally {
      // Always reset isGenerating on ANY exit path (success, abort, error).
      // Without this, a thrown error or session abort would leave isGenerating=true
      // permanently, blocking all future generation attempts.
      if (!isContinuing) {
        this.isGenerating = false;
      }
    }
  }

  /**
   * Sequential generation — preserves style rotation order.
   * Used when multiple styles are present (ordered mode).
   */
  async _launchSequential(portraitsToGenerate, options) {
    const startTime = performance.now();
    let succeeded = 0;
    let failed = 0;

    for (const portrait of portraitsToGenerate) {
      if (!this._isSessionValid()) {
        sentryService.addBreadcrumb(
          'portrait.launch',
          'Sequential batch aborted - session ended',
          {
            remaining:
              portraitsToGenerate.length -
              portraitsToGenerate.indexOf(portrait),
            succeeded,
            failed,
          }
        );
        return;
      }
      await this.generateSingle(portrait, options);
      if (!portrait.isError) {
        succeeded++;
      } else {
        failed++;
      }
    }

    const seqDuration = performance.now() - startTime;
    sentryService.trackMetric(
      'portrait.sequential_batch_duration_ms',
      seqDuration,
      'millisecond',
      {
        portrait_count: String(portraitsToGenerate.length),
      }
    );
    sentryService.incrementCounter('portrait.batch_success', succeeded, {
      mode: 'sequential',
    });
    sentryService.incrementCounter('portrait.batch_error', failed, {
      mode: 'sequential',
    });
    sentryService.addBreadcrumb(
      'portrait.sequential',
      'Sequential batch completed',
      {
        total: portraitsToGenerate.length,
        succeeded,
        failed,
        duration_ms: seqDuration.toFixed(0),
      }
    );
  }

  /**
   * Parallel generation with semaphore-based concurrency control.
   *
   * All portraits are dispatched immediately but the Semaphore gates how many
   * can be in-flight at once (controlled by kiosk.parallel_generations).
   * Display ordering is handled by the consuming UI layer.
   *
   * @param {Array} portraitsToGenerate - Portraits with NEVER_GENERATED status
   * @param {Object} options - Options passed to generateSingle (e.g. { preload: true })
   * @param {number} [maxConcurrent=3] - Maximum simultaneous generation requests
   */
  async _launchParallel(portraitsToGenerate, options, maxConcurrent = 3) {
    const batchStart = performance.now();
    const semaphore = new Semaphore(maxConcurrent);
    const batchResponse = await restApi.reserveConfigs(portraitsToGenerate, {
      repeatWithSeed: options?.repeatWithSeed,
    });
    const selectedConfigs = batchResponse?.configs || [];

    if (selectedConfigs.length !== portraitsToGenerate.length) {
      throw new Error(
        `[PortraitGeneration] reserveConfigs returned ${selectedConfigs.length} configs for ${portraitsToGenerate.length} portraits`
      );
    }

    portraitsToGenerate.forEach((portrait, index) => {
      this.update(portrait.uuid, {
        configUuid: selectedConfigs[index]?.config_uuid || null,
      });
    });

    sentryService.addBreadcrumb('portrait.parallel', 'Parallel batch started', {
      total: portraitsToGenerate.length,
      ...semaphore.getState(),
    });

    const tasks = portraitsToGenerate.map((portrait, index) =>
      semaphore.run(async () => {
        const acquiredAt = performance.now();
        if (!this._isSessionValid()) return;
        await this.generateSingle(portrait, options);
        sentryService.addBreadcrumb(
          'portrait.parallel_item',
          `Portrait ${index + 1}/${portraitsToGenerate.length} completed`,
          {
            uuid: sentryUuid(portrait.uuid),
            succeeded: !portrait.isError,
            wait_ms: (acquiredAt - batchStart).toFixed(0),
            duration_ms: (performance.now() - acquiredAt).toFixed(0),
            semaphore: semaphore.getState(),
          }
        );
      })
    );

    const results = await Promise.allSettled(tasks);
    const fulfilled = results.filter((r) => r.status === 'fulfilled').length;
    const rejected = results.filter((r) => r.status === 'rejected').length;
    const successCount = portraitsToGenerate.filter(
      (p) => p.isPreloaded || p.isGenerated
    ).length;
    const errorCount = portraitsToGenerate.filter((p) => p.isError).length;

    const parallelDuration = performance.now() - batchStart;
    sentryService.trackMetric(
      'portrait.parallel_batch_duration_ms',
      parallelDuration,
      'millisecond',
      {
        portrait_count: String(portraitsToGenerate.length),
        max_concurrent: String(maxConcurrent),
      }
    );
    sentryService.incrementCounter('portrait.batch_success', successCount, {
      mode: 'parallel',
    });
    sentryService.incrementCounter('portrait.batch_error', errorCount, {
      mode: 'parallel',
    });
    sentryService.addBreadcrumb(
      'portrait.parallel',
      'Parallel batch completed',
      {
        total: results.length,
        fulfilled,
        rejected,
        success_count: successCount,
        error_count: errorCount,
        duration_ms: parallelDuration.toFixed(0),
      }
    );
  }

  retry(portrait, options) {
    if (!portrait.isError) {
      return Promise.resolve(portrait);
    }

    // Prevent multiple retries
    if (portrait.isRetrying) {
      return portrait.promise || Promise.resolve(portrait);
    }

    // FIX: Bail out if session ended - don't retry on invalid session
    if (!this._isSessionValid()) {
      sentryService.addBreadcrumb(
        'portrait.retry_skipped',
        'Portrait retry skipped - session ended',
        { uuid: sentryUuid(portrait.uuid) },
        'warning'
      );
      return Promise.resolve(portrait);
    }

    const newRetryCount = (portrait.retryCount || 0) + 1;

    // Wrap retry in Sentry span for performance tracking (safe - never throws)
    return safeSentry.withSpan(
      'portrait.retry_single',
      'portrait.generation',
      async () => {
        // Re-check session validity inside the async callback to close the
        // TOCTOU gap between the synchronous check above and this execution.
        // If reset() fired between the outer check and this callback running,
        // the metrics increment would pollute the new session's counters.
        if (!this._isSessionValid()) {
          return portrait;
        }

        // Track retry metrics and breadcrumb (safe - never throws)
        this._metrics.totalRetries++;
        sentryService.incrementCounter('portrait.retry_initiated', 1, {
          type: 'single',
          style_id: portrait.styleId,
        });
        sentryService.addBreadcrumb(
          'portrait.retry',
          `Retrying single portrait generation (attempt ${newRetryCount})`,
          {
            portrait_uuid: sentryUuid(portrait.uuid),
            style_id: portrait.styleId,
            retry_count: newRetryCount,
            previous_error: portrait.error?.message?.slice(0, 100),
          }
        );

        // CRITICAL: Reset to generating state immediately for UI update
        this.update(portrait.uuid, {
          status: this.STATUSES.GENERATING_NOW,
          error: null,
          isRetrying: true,
          retryCount: newRetryCount,
        });

        return this.generateSingle(portrait, options);
      },
      {
        'portrait.type': 'single',
        'portrait.style_id': portrait.styleId,
        'portrait.retry_count': newRetryCount,
      }
    );
  }

  query(options) {
    const { neverGenerated, preloaded, error } = options;
    return this.portraits.filter(
      (portrait) =>
        (!neverGenerated || portrait.isNeverGenerated) &&
        (!preloaded || portrait.isPreloaded) &&
        (!error || portrait.isError)
    );
  }

  async _generateViaJob(portrait) {
    // The request id is the job id server-side. It survives only a submit
    // that got no answer, so the next attempt re-attaches to the job a lost
    // 202 already started; any answered submit (accepted or refused) and any
    // job that ran get a fresh id, so a failed job is never replayed.
    const requestId = portrait._pendingRequestId || uuidv4();
    this.update(portrait.uuid, { _pendingRequestId: requestId });

    let submitted;
    try {
      submitted = await restApi.submitGeneration({ ...portrait, requestId });
    } catch (error) {
      if (!PortraitGeneration._isSubmitUnanswered(error)) {
        this.update(portrait.uuid, { _pendingRequestId: null });
      }
      throw error;
    }
    if (!submitted?.job_id) {
      throw new Error(
        `Generation submit missing job_id: ${JSON.stringify(submitted)?.slice(0, 200)}`
      );
    }
    this.update(portrait.uuid, { _pendingRequestId: null });

    const jobStatus = await this._pollGenerationJob(submitted.job_id, {
      timeoutMs: this._generationPollTimeoutMs(submitted.budget_s),
    });

    // Pin the config only now, from the job that produced a portrait: a job
    // the worker failed (a refused config, a timeout) leaves the retry free to
    // select again. If the preload after this fails, the retry
    // re-submits pinned to the config the user already saw.
    const configUuid = jobStatus.config_uuid || submitted.config_uuid || null;
    this.update(portrait.uuid, { configUuid });

    return {
      uuid: submitted.job_id,
      url: jobStatus.result_url,
      config_uuid: configUuid,
    };
  }

  // No HTTP status means the request never completed (network failure,
  // request timeout, abort): the backend may hold the job, so the id must be
  // replayed rather than minted anew. A TimeoutError can carry the status of
  // a 202 whose body never arrived — same situation. A 502/503/504 without
  // the backend's error code comes from the proxy, which can lose the 202 of
  // a job that the backend already accepted.
  static _isSubmitUnanswered(error) {
    const response = error?.context?.response;
    const status = httpStatusOf(error);
    if (error?.name === 'TimeoutError' || status === undefined) return true;
    return PROXY_ERROR_STATUSES.has(status) && !response?.json?.code;
  }

  // Two poll intervals of grace past the worker's budget, so a job the
  // worker fails as JOB_TIMEOUT is read from the poll rather than guessed.
  // The budget counts from the moment the worker picks the job up, so the
  // poll clock starts on the first non-pending status; the queue wait before
  // that is bounded separately by GENERATION_QUEUE_WAIT_MAX_MS.
  _generationPollTimeoutMs(
    budgetS,
    fallbackMs = this.GENERATION_POLL_TIMEOUT_MS
  ) {
    if (!(Number.isFinite(budgetS) && budgetS > 0)) {
      return fallbackMs;
    }
    return budgetS * 1000 + this.GENERATION_POLL_MAX_DELAY_MS * 2;
  }

  // Shared by the single-portrait rail and the group-portrait rail: both
  // write the same generated_images row, so both poll the same endpoint with
  // the same terminal interpretation. Only the budget differs.
  async _pollGenerationJob(
    jobId,
    { timeoutMs = this.GENERATION_POLL_TIMEOUT_MS } = {}
  ) {
    const pollStart = Date.now();
    let processingSince = null;
    let delay = this.GENERATION_POLL_INITIAL_DELAY_MS;
    let jobStatus = null;

    for (;;) {
      try {
        jobStatus = await restApi.getGenerationStatus(jobId);
      } catch (pollError) {
        if (pollError?.context?.isRequestManuallyAborted) {
          // abortGenerationRequests()/abortAll() killed this poll on
          // purpose (user moved to payment/printing, session reset).
          // Rethrow so generateSingle's abort handling resets the
          // portrait — swallowing it here would keep the loop polling for
          // up to the full budget.
          throw pollError;
        }
        if (httpStatusOf(pollError) === 404) {
          // Submit persists the row before the 202, so a 404 means the id
          // was never accepted — terminate instead of polling forever.
          throw new Error(
            'Generation job unknown to backend - submit was never accepted'
          );
        }
        // Transient (network blip, brief backend restart) — keep polling
        // until the budget; the job keeps generating server-side.
      }
      if (!this._isSessionValid()) {
        throw new Error('Session changed during generation polling');
      }
      if (jobStatus?.is_final) {
        break;
      }
      // The worker's budget runs from pickup, not from the 202: a job still
      // queued (or re-queued by a worker restart) has not spent any of it.
      if (jobStatus?.status === 'pending') {
        processingSince = null;
      } else if (jobStatus && processingSince === null) {
        processingSince = Date.now();
      }
      const now = Date.now();
      const totalElapsed = now - pollStart;
      // No status yet (the poll itself keeps failing): the plain clock. A
      // queued job spends none of the budget; a running one spends it from
      // the moment it was seen running.
      let budgetElapsed = totalElapsed;
      if (jobStatus) {
        budgetElapsed = processingSince === null ? 0 : now - processingSince;
      }
      if (
        budgetElapsed > timeoutMs ||
        totalElapsed > timeoutMs + this.GENERATION_QUEUE_WAIT_MAX_MS
      ) {
        throw new Error(
          `Generation not finished after ${Math.round(totalElapsed / 1000)}s (last status: ${jobStatus?.status || 'unknown'})`
        );
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 1.5, this.GENERATION_POLL_MAX_DELAY_MS);
    }

    if (jobStatus?.status !== 'ready' || !jobStatus?.result_url) {
      const failure = new Error(
        jobStatus?.error ||
          `Generation failed with status: ${jobStatus?.status || 'unknown'}`
      );
      // The worker's reason (NO_FACE_DETECTED, JOB_TIMEOUT, ...) for callers
      // that branch on it rather than on the message.
      failure.code = jobStatus?.error || null;
      throw failure;
    }

    return jobStatus;
  }

  // The pinned config names a workflow that cannot run, for example after an
  // operator deactivated it. The same pin gets the same answer, but a submit
  // with no pin selects a config whose workflow runs.
  static _isConfigWorkflowUnavailable(error) {
    return (
      error?.context?.response?.json?.code ===
        'V1_GENERATIONS_CONFIG_WORKFLOW_UNAVAILABLE' ||
      error?.code === 'GENERATION_WORKFLOW_UNAVAILABLE'
    );
  }

  static _isGroupPortraitsNotConfigured(error) {
    return (
      httpStatusOf(error) === 404 &&
      error?.context?.response?.json?.code ===
        'V1_GROUP_PORTRAIT_NOT_CONFIGURED'
    );
  }

  // A person of the group lacks a trait. The same people get the same answer.
  static _isGroupSubjectInvalid(error) {
    return (
      httpStatusOf(error) === 422 &&
      error?.context?.response?.json?.code ===
        'V1_GROUP_PORTRAIT_SUBJECT_INVALID'
    );
  }

  // A job whose group does not fit the cast of its config fails with this
  // code, for example after an edit of the config. A new attempt gets the
  // same answer.
  static _isGroupCastMismatch(error) {
    return (
      error?.context?.response?.json?.code === 'WORKFLOW_CAST_MISMATCH' ||
      error?.code === 'WORKFLOW_CAST_MISMATCH'
    );
  }

  // The group key is in the selection key: a visitor who corrects an age or
  // a gender makes a different group, which a different story can take.
  static _groupRatioOf(outputSize) {
    const width = Number(outputSize?.width);
    const height = Number(outputSize?.height);
    return width > 0 && height > 0 && Number.isFinite(width / height)
      ? width / height
      : GROUP_PORTRAIT_DEFAULT_RATIO;
  }

  _groupSelectionKey(faces, styleId) {
    const faceIds = faces
      .map((face) => face.uuid)
      .sort()
      .join(',');
    return `${styleId}_${faceIds}_${groupKey(faces)}`;
  }

  isGroupSelectionSkipped(faces, styleId) {
    return this._skippedGroupSelections.has(
      this._groupSelectionKey(faces, styleId)
    );
  }

  // These answers end the group portraits of this face selection: the singles
  // run on, with no retry and no group tile, and the visitor sees no message.
  _skipGroupPortraitsOn(error, faces, styleId) {
    if (
      !(
        PortraitGeneration._isGroupPortraitsNotConfigured(error) ||
        PortraitGeneration._isGroupSubjectInvalid(error) ||
        PortraitGeneration._isGroupCastMismatch(error)
      )
    ) {
      return;
    }
    this._skippedGroupSelections.add(this._groupSelectionKey(faces, styleId));
    error.groupPortraitSkipped = true;
    sentryService.addBreadcrumb(
      'portrait.group_not_offered',
      'No group portrait for this group',
      {
        code: error?.context?.response?.json?.code ?? error?.code ?? 'unknown',
        style_id: styleId,
        face_count: faces.length,
      },
      'info'
    );
  }

  _removeGroupPortrait(uuid) {
    const index = this.groupPortraits.findIndex((p) => p.uuid === uuid);
    if (index !== -1) {
      this.groupPortraits.splice(index, 1);
    }
  }

  // One scene containing everyone, generated by the workflow graph the
  // deployment runs. Same submit → poll shape as the single rail.
  // `requestId` is the job id: a repeated submit with it gets the same job,
  // so the automatic retry after a lost 202 starts nothing.
  // `onAccepted` runs when the submit answers 202, before the poll: the
  // backend takes the group, thus the kiosk can show its tile. It gets the
  // ratio of the image that the workflow makes.
  async _requestGroupPortrait(faces, styleId, requestId, onAccepted) {
    try {
      kioskJourney.once('generation_started', {
        portrait_kind: 'group',
        faces_count: faces.length,
      });
      const submitted = await restApi.submitGroupPortrait({
        faces: orderPeopleLeftToRight(faces, store.photos ?? []),
        style_id: styleId,
        bucket_name: store.kiosk?.portrait_s3_bucket_name,
        request_id: requestId,
      });
      if (!submitted?.job_id) {
        throw new Error(
          `Group portrait submit missing job_id: ${JSON.stringify(submitted)?.slice(0, 200)}`
        );
      }
      onAccepted?.(PortraitGeneration._groupRatioOf(submitted.output_size));
      const jobStatus = await this._pollGenerationJob(submitted.job_id, {
        timeoutMs: this._generationPollTimeoutMs(
          submitted.budget_s,
          this.GROUP_PORTRAIT_POLL_TIMEOUT_MS
        ),
      });
      return { uuid: submitted.job_id, url: jobStatus.result_url };
    } catch (error) {
      this._skipGroupPortraitsOn(error, faces, styleId);
      throw error;
    }
  }

  /**
   * The function downloads the image of a generated portrait and sends the
   * download time as a metric. The returned promise gives the value that
   * onLoaded returns.
   *
   * onLoaded must do its own check of the session. An async onLoaded adds
   * two microtasks to the chain. If the download or onLoaded gives an error,
   * the function sets the portrait to ERROR.
   *
   * @param {Object} portrait - The portrait that has a generated image
   * @param {Object} options
   * @param {() => Object} options.metricTags - Gives the tags of the metric
   * @param {string} options.logLabel - The first argument of the console error
   * @param {string} options.failCategory - The category of the error breadcrumb
   * @param {string} options.failMessage - The message of the error breadcrumb
   * @param {() => Object} [options.errorPatch] - Gives more fields for the ERROR update
   * @param {(imageUrl: string) => unknown} options.onLoaded - Runs after the download
   * @returns {Promise<unknown>}
   */
  _preloadPortrait(
    portrait,
    { metricTags, logLabel, failCategory, failMessage, errorPatch, onLoaded }
  ) {
    const preloadStartTime = performance.now();
    // Only the blob_url_cleaned_up breadcrumb field reads this value. The
    // field is true if the download completed before the error.
    let loadedImageUrl = null;

    return fetchAuthenticatedImage(portrait.url)
      .then((imageUrl) => {
        loadedImageUrl = imageUrl;

        // Track preload duration
        const preloadDuration = performance.now() - preloadStartTime;
        sentryService.trackMetric(
          'portrait.preload_duration_ms',
          preloadDuration,
          'millisecond',
          metricTags()
        );

        return onLoaded(imageUrl);
      })
      .catch((preloadError) => {
        // If session ended, skip logging and state update
        if (!this._isSessionValid()) {
          return portrait;
        }

        // Image generated but failed to preload — set to ERROR so the retry
        // button appears. Without this, portrait stays in GENERATED_NOT_PRELOADED
        // forever with a "Loading..." placeholder and no way to recover.
        console.error(logLabel, {
          error: preloadError,
          url: portrait.url,
          uuid: portrait.uuid,
        });
        sentryService.addBreadcrumb(
          failCategory,
          failMessage,
          {
            uuid: sentryUuid(portrait.uuid),
            error: preloadError?.message,
            blob_url_cleaned_up: !!loadedImageUrl,
          },
          'error'
        );
        return this.update(portrait.uuid, {
          status: this.STATUSES.ERROR,
          isError: true,
          isRetrying: false,
          ...errorPatch?.(),
          error: {
            message: preloadError?.message || 'Image preload failed',
            timestamp: Date.now(),
            retryCount: portrait.retryCount || 0,
          },
        });
      });
  }

  generateSingle(portrait, options) {
    const analyticsVisitId = kioskJourney.id;
    const startTime = performance.now();
    const retryCount = portrait.retryCount || 0;

    // Sentry breadcrumb for generation start (safe - never throws)
    sentryService.addBreadcrumb(
      'portrait.generate',
      'Starting portrait generation',
      {
        uuid: sentryUuid(portrait.uuid),
        face_uuid: sentryUuid(portrait.faceUuid),
        style_id: portrait.styleId,
        retry_count: retryCount,
        priority: portrait.priority,
      }
    );

    // Wrap generation in Sentry span for performance tracking (safe - never throws)
    const promise = safeSentry
      .withSpan(
        'portrait.generate_single',
        'ai.generation',
        async () => {
          kioskJourney.once('generation_started', {
            portrait_kind: 'individual',
          });
          const response = await this._generateViaJob(portrait);

          // Defense-in-depth: if the response somehow lacks uuid/url, fail
          // early instead of setting portrait to GENERATED with undefined URL.
          if (!(response?.uuid && response?.url)) {
            throw new Error(
              `Generation response missing uuid/url: ${JSON.stringify(response)?.slice(0, 200)}`
            );
          }

          nextTick(() => this.trigger(this.EVENTS.GENERATED_SINGLE, portrait));
          return this.update(portrait.uuid, {
            uuid: response.uuid,
            configUuid: response.config_uuid || null,
            url: response.url,
            status: this.STATUSES.GENERATED_NOT_PRELOADED,
          });
        },
        {
          'portrait.uuid': sentryUuid(portrait.uuid),
          'portrait.face_uuid': sentryUuid(portrait.faceUuid),
          'portrait.style_id': portrait.styleId,
          'portrait.retry_count': retryCount,
          'portrait.priority': portrait.priority,
        }
      )
      .then((portrait) => {
        // Guard: update() returns null if portrait was cleared by reset()
        if (!(portrait && options?.preload)) {
          return portrait;
        }

        // Skip preload if session ended during generation -- avoids orphaned
        // image downloads that waste bandwidth on a kiosk with limited network.
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.preload_skipped',
            'Skipping preload - session ended during generation',
            { uuid: sentryUuid(portrait.uuid) },
            'warning'
          );
          return portrait;
        }

        return this._preloadPortrait(portrait, {
          metricTags: () => ({
            type: 'single',
            style_id: portrait.styleId,
          }),
          logLabel: '[Portrait] Preload failed:',
          failCategory: 'portrait.preload_failed',
          failMessage: 'Portrait preload failed - setting to ERROR for retry',
          errorPatch: () => ({ _completedAt: Date.now() }),
          onLoaded: async (imageUrl) => {
            // Session may have ended during the fetch -- bail out before updating
            // cleared portrait arrays to avoid null returns and wasted state updates.
            if (!this._isSessionValid()) {
              return portrait;
            }

            // Reveal through the pacer, not directly: the image is ready, but
            // showing it is held to a minimum gap so a burst of parallel
            // completions doesn't snap several tiles into the gallery at once.
            // The pacer runs this synchronously when the gap has already
            // elapsed, preserving the one-by-one progressive reveal that
            // deferring via nextTick used to destroy by batching.
            this._revealPacer.reveal(() => {
              // Re-check inside the callback: the wait is real time, so the
              // session can end between enqueue and reveal.
              if (!this._isSessionValid()) return;
              const updated = this.update(portrait.uuid, {
                status: this.STATUSES.GENERATED_AND_PRELOADED,
                isRetrying: false,
                _originalUrl: portrait.url,
                url: imageUrl,
                _completedAt: Date.now(),
              });
              // Guard: update() returns null if portrait was cleared by reset()
              // between the session check above and here (TOCTOU window).
              if (updated) {
                this.trigger(
                  this.EVENTS.GENERATED_AND_PRELOADED_SINGLE,
                  updated
                );
              }
            });
            // update() mutates this same object in place, so the caller's
            // reference stays correct once the reveal lands.
            return portrait;
          },
        });
      })
      .then((result) => {
        // FIX: Bail out if session ended - don't update metrics for orphaned operations
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.orphaned',
            'Portrait generation completed after session ended, skipping metrics',
            { uuid: sentryUuid(portrait.uuid) },
            'warning'
          );
          return result;
        }

        // Track successful generation metrics (safe - never throws)
        const duration = performance.now() - startTime;
        this._metrics.totalGenerated++;
        sentryService.incrementCounter('portrait.generated', 1, {
          style_id: portrait.styleId,
          is_retry: retryCount > 0,
        });
        sentryService.trackMetric(
          'portrait.generation_duration_ms',
          duration,
          'millisecond',
          {
            style_id: portrait.styleId,
          }
        );
        sentryService.addBreadcrumb(
          'portrait.generated',
          `Portrait generated successfully in ${duration.toFixed(0)}ms`,
          {
            uuid: sentryUuid(portrait.uuid),
            duration_ms: duration.toFixed(0),
            retry_count: retryCount,
          }
        );
        this._updateSentryContext();
        return result;
      })
      .catch(async (error) => {
        // Handle intentional abort (e.g., user navigated away) - don't mark as error
        if (this.isAbortError(error)) {
          sentryService.addBreadcrumb(
            'portrait.aborted',
            'Portrait generation aborted by user',
            {
              uuid: sentryUuid(portrait.uuid),
              session_valid: this._isSessionValid(),
            }
          );

          // FIX: If session ended (reset() was called), bail out entirely
          // The portrait array is already cleared, no state to update
          if (!this._isSessionValid()) {
            return null;
          }

          // Reset to NEVER_GENERATED so it can be regenerated if user returns
          return this.update(portrait.uuid, {
            status: this.STATUSES.NEVER_GENERATED,
            isRetrying: false,
            error: null,
          });
          // NOTE: Do NOT reset isGenerating here. It is owned by launch()'s finally
          // block. Resetting it inside a single portrait's abort handler would allow
          // a competing launch() call to slip through the entry guard, causing two
          // concurrent batch generations.
        }

        // The error holds the request body with the traits of the person.
        console.error(
          `[Portrait] Generation failed${retryCount > 0 ? ` (attempt ${retryCount})` : ''}:`,
          {
            uuid: portrait.uuid,
            faceUuid: portrait.faceUuid,
            styleId: portrait.styleId,
            error: errorLogSummary(error),
          }
        );

        // FIX: If session ended, skip metrics and state updates to prevent pollution
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.orphaned_error',
            'Portrait generation failed after session ended, skipping state update',
            {
              uuid: sentryUuid(portrait.uuid),
              error: error?.message,
            },
            'warning'
          );
          return null;
        }

        // Track failed generation metrics (safe - never throws)
        kioskJourney.record(
          'generation_failed',
          {
            portrait_kind: 'individual',
            ...kioskErrorProperties(error, 'generation'),
          },
          analyticsVisitId
        );
        this._metrics.totalFailed++;
        sentryService.incrementCounter('portrait.failed', 1, {
          style_id: portrait.styleId,
          is_retry: retryCount > 0,
        });

        const emptyFrame = isEmptyFrameError(error);

        // Capture error to Sentry with full context (safe - never throws)
        // NOTE: Demographic data (age_group, gender, race) intentionally excluded
        // to comply with GDPR/CCPA/BIPA privacy regulations
        if (!emptyFrame) {
          sentryService.captureException(error, {
            tags: {
              feature: 'portrait_generation',
              generation_type: 'single',
              error_type: safeSentry.categorizeError(error),
              style_id: portrait.styleId,
              retry_count: String(retryCount),
            },
            extra: {
              session_id: sentryUuid(store.sessionId),
              portrait_uuid: sentryUuid(portrait.uuid),
              face_uuid: sentryUuid(portrait.faceUuid),
              style_id: portrait.styleId,
              priority: portrait.priority,
              retry_count: retryCount,
              photo_count: store.photos?.length || 0,
              duration_ms: performance.now() - startTime,
              has_demographics: !!(
                portrait.age_group ||
                portrait.gender ||
                portrait.race
              ),
            },
          });
        }

        sentryService.addBreadcrumb(
          'portrait.failed',
          `Portrait generation failed: ${error?.message}`,
          {
            uuid: sentryUuid(portrait.uuid),
            error: error?.message,
            retry_count: retryCount,
          },
          'error'
        );
        this._updateSentryContext();

        // The auto-retry and a manual retry then select a config again.
        if (
          this.getByUuid(portrait.uuid)?.configUuid &&
          PortraitGeneration._isConfigWorkflowUnavailable(error)
        ) {
          this.update(portrait.uuid, { configUuid: null });
        }

        // Auto-retry once silently on first failure (improves UX for transient
        // errors). An empty frame is not transient — same photo, same verdict.
        if (retryCount === 0 && !emptyFrame && this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.auto_retry',
            'Auto-retrying portrait generation after first failure',
            {
              uuid: sentryUuid(portrait.uuid),
              original_error: error?.message,
            },
            'info'
          );
          sentryService.incrementCounter('portrait.auto_retry', 1, {
            style_id: portrait.styleId,
            error_type: safeSentry.categorizeError(error),
          });

          // Update retryCount and trigger silent retry.
          // update() returns null if portrait was already cleared by reset().
          const updatedPortrait = this.update(portrait.uuid, {
            retryCount: 1,
            isRetrying: false, // Keep false to not show "Retrying..." to user
          });

          // Bail out if portrait was cleared by reset() between the session check
          // above and the update() call (TOCTOU window).
          if (!(updatedPortrait && this._isSessionValid())) {
            return null;
          }

          // Small delay before auto-retry to avoid hammering server
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Re-check session validity after delay -- reset() may have fired
          if (this._isSessionValid()) {
            return this.generateSingle(updatedPortrait, options);
          }
        }

        return this.update(portrait.uuid, {
          status: this.STATUSES.ERROR,
          isRetrying: false,
          _completedAt: Date.now(),
          error: {
            message: error?.message || 'Failed to generate portrait',
            details: error?.cause || error,
            timestamp: new Date().toISOString(),
            retryCount: retryCount,
            // Lets the UI offer "retake" rather than "try again".
            noFaceDetected: emptyFrame,
          },
        });
      })
      .finally(() => {
        // FIX: Only update if session is still valid - portrait may not exist after reset()
        if (this._isSessionValid()) {
          this.update(portrait.uuid, { isRetrying: false });
        }
      });

    this.update(portrait.uuid, {
      status: this.STATUSES.GENERATING_NOW,
      promise,
    });
    return promise;
  }

  generateGroupPortrait(
    faces,
    styleIdOverride = null,
    _internalRetryCount = 0,
    requestId = uuidv4()
  ) {
    if (faces.length === 0) {
      useUiStore().showError('portrait_generation_faces_not_selected');
      throw new Error('portrait_generation_faces_not_selected');
    }

    // Use override style if provided, otherwise use selected style
    const styleId = styleIdOverride || store.selectedStyle?.id;
    if (!styleId) {
      useUiStore().showError('portrait_generation_style_not_selected');
      throw new Error('portrait_generation_style_not_selected');
    }

    // No group config takes this selection: a new submit gets the same answer.
    if (this.isGroupSelectionSkipped(faces, styleId)) {
      return Promise.resolve(null);
    }

    // Capture current session ID for stale-result detection
    this._generationSessionId = store.sessionId;

    // Capture the current group generation epoch. If ensureInitialGroupPortraits()
    // starts a new batch while this generation is in-flight, the epoch will advance
    // and this generation's results should be discarded as stale.
    const capturedEpoch = this._groupGenerationEpoch;
    const analyticsVisitId = kioskJourney.id;
    const isStale = () =>
      !this._isSessionValid() || capturedEpoch !== this._groupGenerationEpoch;

    const startTime = performance.now();
    const faceUuids = faces.map((face) => face.uuid);

    // The tile of the group portrait, with a temp UUID for tracking. It goes
    // into the gallery only when the submit answers 202: a group that no
    // config takes gets no tile.
    const groupPortrait = this.normalize({
      // uuid will be auto-generated by normalize() for tracking
      faceUuids: faceUuids,
      styleId: styleId,
      isGroupPortrait: true,
      status: this.STATUSES.GENERATING_NOW,
      retryCount: _internalRetryCount,
    });

    // Sentry breadcrumb for group generation start (safe - never throws)
    sentryService.addBreadcrumb(
      'portrait.group_generate',
      'Starting group portrait generation',
      {
        uuid: sentryUuid(groupPortrait.uuid),
        face_count: faces.length,
        face_uuids: safeSentry.sanitizeFaceUuids(faceUuids),
        style_id: styleId,
      }
    );

    let tileShown = false;
    let accepted = false;
    const showTile = (aspectRatio) => {
      accepted = true;
      // A 202 that arrives after the session or the batch changed shows nothing.
      if (tileShown || isStale()) {
        return;
      }
      tileShown = true;
      groupPortrait.aspectRatio = aspectRatio;
      this.groupPortraits.push(groupPortrait);
      this.groupGeneratingCount++;
      this._updateSentryContextDebounced();
    };

    // Track whether the catch handler already decremented groupGeneratingCount
    // to prevent the finally block from double-decrementing on auto-retry.
    let countAlreadyDecrementedInCatch = false;
    const dropTile = () => {
      const index = this.groupPortraits.findIndex(
        (p) => p.uuid === groupPortrait.uuid
      );
      if (index !== -1) {
        this.groupPortraits.splice(index, 1);
        this.groupGeneratingCount--;
        // Prevent finally block from double-decrementing
        countAlreadyDecrementedInCatch = true;
      }
    };

    // Wrap generation in Sentry span for performance tracking (safe - never throws)
    const promise = safeSentry
      .withSpan(
        'portrait.generate_group',
        'ai.generation',
        async () => {
          const response = await this._requestGroupPortrait(
            faces,
            styleId,
            requestId,
            showTile
          );

          // Defense-in-depth: fail early if response lacks required fields
          if (!(response?.uuid && response?.url)) {
            throw new Error(
              `Group generation response missing uuid/url: ${JSON.stringify(response)?.slice(0, 200)}`
            );
          }

          return this.update(groupPortrait.uuid, {
            url: response.url,
            uuid: response.uuid,
            status: this.STATUSES.GENERATED_NOT_PRELOADED,
          });
        },
        {
          'portrait.uuid': sentryUuid(groupPortrait.uuid),
          'portrait.face_count': faces.length,
          'portrait.style_id': styleId,
          'portrait.is_group': true,
        }
      )
      .then((portrait) => {
        // update() returns null when portrait was already cleared by reset().
        // Bail out to avoid null dereference in preload chain below.
        if (!portrait) return null;

        // Check if a newer batch of group generations was started while this
        // one was in-flight. The epoch advances when ensureInitialGroupPortraits()
        // starts a new batch, making this result stale.
        if (capturedEpoch !== this._groupGenerationEpoch) {
          sentryService.addBreadcrumb(
            'portrait.group_stale_epoch',
            'Group portrait result discarded - newer batch started',
            {
              uuid: sentryUuid(portrait.uuid),
              captured_epoch: capturedEpoch,
              current_epoch: this._groupGenerationEpoch,
            },
            'warning'
          );
          // Remove the stale portrait from groupPortraits array
          this._removeGroupPortrait(groupPortrait.uuid);
          return null;
        }

        // Skip preload if session ended during generation -- avoids orphaned
        // image downloads that waste bandwidth on a kiosk with limited network.
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.group_preload_skipped',
            'Skipping group preload - session ended during generation',
            { uuid: sentryUuid(portrait.uuid) },
            'warning'
          );
          return portrait;
        }

        return this._preloadPortrait(portrait, {
          metricTags: () => ({
            type: 'group',
            style_id: portrait.styleId,
            face_count: String(faces.length),
          }),
          logLabel: '[GroupPortrait] Preload failed:',
          failCategory: 'portrait.group_preload_failed',
          failMessage:
            'Group portrait preload failed - setting to ERROR for retry',
          onLoaded: (imageUrl) => {
            // Session may have ended during the fetch
            if (!this._isSessionValid()) {
              return portrait;
            }

            nextTick(() =>
              this.trigger(this.EVENTS.GROUP_GENERATED_AND_PRELOADED, portrait)
            );
            return this.update(portrait.uuid, {
              status: this.STATUSES.GENERATED_AND_PRELOADED,
              _originalUrl: portrait.url,
              url: imageUrl,
            });
          },
        });
      })
      .then((result) => {
        // Bail out if session ended or epoch advanced (stale batch result)
        if (isStale()) {
          sentryService.addBreadcrumb(
            'portrait.group_orphaned',
            'Group portrait completed after session ended or batch replaced, skipping metrics',
            { uuid: sentryUuid(groupPortrait.uuid) },
            'warning'
          );
          return result;
        }

        // Track successful group generation metrics (safe - never throws)
        const duration = performance.now() - startTime;
        this._metrics.groupGenerated++;
        sentryService.incrementCounter('portrait.group_generated', 1, {
          style_id: styleId,
          face_count: String(faces.length),
        });
        sentryService.trackMetric(
          'portrait.group_generation_duration_ms',
          duration,
          'millisecond',
          {
            style_id: styleId,
            face_count: String(faces.length),
          }
        );
        sentryService.addBreadcrumb(
          'portrait.group_generated',
          `Group portrait generated successfully in ${duration.toFixed(0)}ms`,
          {
            uuid: sentryUuid(groupPortrait.uuid),
            duration_ms: duration.toFixed(0),
            face_count: faces.length,
          }
        );
        this._updateSentryContext();
        return result;
      })
      .catch(async (error) => {
        // Handle intentional abort (e.g., user navigated away) - remove incomplete portrait
        if (this.isAbortError(error)) {
          sentryService.addBreadcrumb(
            'portrait.group_aborted',
            'Group portrait generation aborted by user',
            {
              uuid: sentryUuid(groupPortrait.uuid),
              session_valid: this._isSessionValid(),
            }
          );

          // FIX: If session ended (reset() was called), bail out - array already cleared
          if (!this._isSessionValid()) {
            return null;
          }

          // Remove the incomplete group portrait from the array
          this._removeGroupPortrait(groupPortrait.uuid);
          return null;
        }

        // Skip metrics and state updates if session ended or batch was replaced
        if (isStale()) {
          sentryService.addBreadcrumb(
            'portrait.group_orphaned_error',
            'Group portrait failed after session ended or batch replaced, skipping state update',
            {
              uuid: sentryUuid(groupPortrait.uuid),
              error: error?.message,
            },
            'warning'
          );
          // Remove stale portrait from array if still present
          this._removeGroupPortrait(groupPortrait.uuid);
          return null;
        }

        // No story takes this group, a person lacks a trait, or the job does
        // not fit the cast (_skipGroupPortraitsOn): an expected answer with
        // its own breadcrumb. No retry, no tile, no error event.
        if (error?.groupPortraitSkipped) {
          dropTile();
          // The staggered submits of this batch would only get the same
          // answer a minute later; cancel their wait so the step moves on.
          this._groupGenerationController?.abort();
          return null;
        }

        // The error object carries the request body, thus the people of the
        // group. Only its summary goes to the console.
        console.error('[GroupPortrait] Generation failed:', {
          faceUuids: groupPortrait.faceUuids,
          styleId: groupPortrait.styleId,
          error: errorLogSummary(error),
        });

        // Track failed group generation metrics (safe - never throws)
        kioskJourney.record(
          'generation_failed',
          {
            portrait_kind: 'group',
            ...kioskErrorProperties(error, 'generation'),
          },
          analyticsVisitId
        );
        this._metrics.groupFailed++;
        sentryService.incrementCounter('portrait.group_failed', 1, {
          style_id: styleId,
          face_count: String(faces.length),
        });

        const emptyFrame = isEmptyFrameError(error);

        // Capture error to Sentry with full context (safe - never throws)
        // NOTE: Demographic data (age_group, gender, race) intentionally excluded
        // to comply with GDPR/CCPA/BIPA privacy regulations.
        if (!emptyFrame) {
          sentryService.captureException(error, {
            tags: {
              feature: 'portrait_generation',
              generation_type: 'group',
              error_type: safeSentry.categorizeError(error),
              style_id: styleId,
              face_count: String(faces.length),
            },
            extra: {
              session_id: sentryUuid(store.sessionId),
              portrait_uuid: sentryUuid(groupPortrait.uuid),
              face_uuids: safeSentry.sanitizeFaceUuids(faceUuids),
              style_id: styleId,
              face_count: faces.length,
              photo_count: store.photos?.length || 0,
              faces_with_demographics: faces.filter(
                (f) => f.age_group || f.gender
              ).length,
              duration_ms: performance.now() - startTime,
            },
          });
        }

        sentryService.addBreadcrumb(
          'portrait.group_failed',
          `Group portrait generation failed: ${error?.message}`,
          {
            uuid: sentryUuid(groupPortrait.uuid),
            error: error?.message,
            face_count: faces.length,
          },
          'error'
        );
        this._updateSentryContext();

        // Auto-retry once silently on first failure (improves UX for transient
        // errors). An empty frame is not transient — same photo, same verdict.
        // Skip auto-retry if epoch advanced (a newer batch replaced this one).
        const currentRetryCount = groupPortrait.retryCount || 0;
        if (currentRetryCount === 0 && !emptyFrame && !isStale()) {
          sentryService.addBreadcrumb(
            'portrait.group_auto_retry',
            'Auto-retrying group portrait generation after first failure',
            {
              uuid: sentryUuid(groupPortrait.uuid),
              original_error: error?.message,
              face_count: faces.length,
            },
            'info'
          );
          sentryService.incrementCounter('portrait.group_auto_retry', 1, {
            style_id: styleId,
            face_count: String(faces.length),
            error_type: safeSentry.categorizeError(error),
          });

          // A submit that failed before its 202 has no tile to update.
          if (tileShown) {
            // Update retryCount. update() returns null if portrait was cleared by reset().
            const updatedGroupPortrait = this.update(groupPortrait.uuid, {
              retryCount: 1,
            });

            // Bail out if portrait was cleared by reset() between the session check
            // above and the update() call (TOCTOU window).
            if (!(updatedGroupPortrait && this._isSessionValid())) {
              return null;
            }
          }

          // Small delay before auto-retry to avoid hammering server
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Re-check session validity after delay -- reset() may have fired
          if (this._isSessionValid()) {
            // Remove from groupPortraits (will be re-added by generateGroupPortrait)
            dropTile();
            // Pass retryCount=1 to prevent infinite retry loop. The request
            // id is the job id: a submit that got no answer keeps it, thus
            // the retry re-attaches to a job that a lost 202 started. A job
            // that ran and failed gets a new id, or the backend answers with
            // the same failed job.
            const retryRequestId =
              !accepted && PortraitGeneration._isSubmitUnanswered(error)
                ? requestId
                : uuidv4();
            return this.generateGroupPortrait(
              faces,
              styleIdOverride,
              1,
              retryRequestId
            );
          }
        }

        // A submit that never answered 202 has no tile to mark.
        if (!tileShown) {
          return null;
        }

        return this.update(groupPortrait.uuid, {
          status: this.STATUSES.ERROR,
          error: {
            message: error?.message || 'Failed to generate group portrait',
            details: error?.cause || error,
            timestamp: new Date().toISOString(),
          },
        });
      })
      .finally(() => {
        // Skip if session ended (reset() already zeroed groupGeneratingCount),
        // if the auto-retry catch handler already decremented the count, or
        // if the tile never showed: the count goes up only with the tile.
        if (
          !this._isSessionValid() ||
          countAlreadyDecrementedInCatch ||
          !tileShown
        ) {
          return;
        }

        // Decrement the generating count (was incremented with the tile).
        // This runs for both current-epoch and stale-epoch results.
        this.groupGeneratingCount--;

        // Only update portrait state if this result is from the current epoch.
        // Stale-epoch results have already been removed from groupPortraits.
        if (capturedEpoch === this._groupGenerationEpoch) {
          this.update(groupPortrait.uuid, { isRetrying: false });
        }
      });

    // The tile goes into groupPortraits only on the 202, thus update() does
    // not find it at this point.
    groupPortrait.promise = promise;
    return promise;
  }

  retryGroupPortrait(portrait) {
    if (!portrait.isError) {
      return Promise.resolve(portrait);
    }

    // Prevent multiple retries
    if (portrait.isRetrying) {
      return portrait.promise || Promise.resolve(portrait);
    }

    if ((portrait.retryCount || 0) >= this.MAX_RETRY_COUNT) {
      sentryService.addBreadcrumb(
        'portrait.group_retry_blocked',
        'Group portrait retry blocked - retry limit reached',
        {
          uuid: sentryUuid(portrait.uuid),
          retry_count: portrait.retryCount,
        },
        'warning'
      );
      return Promise.resolve(portrait);
    }

    // FIX: Bail out if session ended - don't retry on invalid session
    if (!this._isSessionValid()) {
      sentryService.addBreadcrumb(
        'portrait.group_retry_skipped',
        'Group portrait retry skipped - session ended',
        { uuid: sentryUuid(portrait.uuid) },
        'warning'
      );
      return Promise.resolve(portrait);
    }

    const newRetryCount = (portrait.retryCount || 0) + 1;
    const startTime = performance.now();

    // Track retry metrics and breadcrumb (safe - never throws)
    this._metrics.totalRetries++;
    sentryService.incrementCounter('portrait.group_retry', 1, {
      style_id: portrait.styleId,
    });
    sentryService.addBreadcrumb(
      'portrait.group_retry',
      `Retrying group portrait generation (attempt ${newRetryCount})`,
      {
        uuid: sentryUuid(portrait.uuid),
        style_id: portrait.styleId,
        retry_count: newRetryCount,
        previous_error: portrait.error?.message?.slice(0, 100),
      }
    );

    // CRITICAL: Reset to generating state immediately for UI update
    this.update(portrait.uuid, {
      status: this.STATUSES.GENERATING_NOW,
      error: null,
      isRetrying: true,
      retryCount: newRetryCount,
    });

    // We need to get the actual face objects from faceUuids
    // Validate BEFORE incrementing counter to prevent counter corruption on error
    const faces = portrait.faceUuids
      .map((uuid) => store.faces.find((face) => face.uuid === uuid))
      .filter(Boolean);

    // Validate we have faces before proceeding
    if (faces.length === 0) {
      console.error(
        '[PortraitGeneration] Cannot retry group portrait: no faces found in store for UUIDs',
        portrait.faceUuids
      );
      const facesNotFoundError = new Error(
        'Cannot retry group portrait: faces not found'
      );
      sentryService.captureException(facesNotFoundError, {
        tags: {
          feature: 'portrait_generation',
          generation_type: 'group_retry',
          error_type: safeSentry.categorizeError(facesNotFoundError),
        },
        extra: {
          session_id: sentryUuid(store.sessionId),
          portrait_uuid: sentryUuid(portrait.uuid),
          face_uuids: safeSentry.sanitizeFaceUuids(portrait.faceUuids),
          retry_count: newRetryCount,
        },
      });
      this.update(portrait.uuid, { isRetrying: false });
      throw new Error('Cannot retry portrait generation: face data not found');
    }

    // Only increment counter after validation passes
    this.groupGeneratingCount++;
    this._updateSentryContextDebounced();

    // Wrap retry in Sentry span (safe - never throws)
    const promise = safeSentry
      .withSpan(
        'portrait.retry_group',
        'ai.generation',
        async () => {
          // A visitor's retry is a new attempt, so a new job.
          const response = await this._requestGroupPortrait(
            faces,
            portrait.styleId,
            uuidv4(),
            (aspectRatio) => {
              if (this._isSessionValid()) {
                this.update(portrait.uuid, { aspectRatio });
              }
            }
          );

          if (!(response?.uuid && response?.url)) {
            throw new Error(
              `Group generation retry response missing uuid/url: ${JSON.stringify(response)?.slice(0, 200)}`
            );
          }

          return this.update(portrait.uuid, {
            url: response.url,
            uuid: response.uuid,
            status: this.STATUSES.GENERATED_NOT_PRELOADED,
          });
        },
        {
          'portrait.uuid': sentryUuid(portrait.uuid),
          'portrait.style_id': portrait.styleId,
          'portrait.retry_count': newRetryCount,
          'portrait.is_group': true,
        }
      )
      .then((updatedPortrait) => {
        // update() returns null when portrait was already cleared by reset()
        if (!updatedPortrait) return null;

        // Skip preload if session ended during generation
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.group_retry_preload_skipped',
            'Skipping group retry preload - session ended during generation',
            { uuid: sentryUuid(updatedPortrait.uuid) },
            'warning'
          );
          return updatedPortrait;
        }

        return this._preloadPortrait(updatedPortrait, {
          metricTags: () => ({
            type: 'group_retry',
            style_id: updatedPortrait.styleId,
          }),
          logLabel: '[GroupPortrait] Retry preload failed:',
          failCategory: 'portrait.group_retry_preload_failed',
          failMessage: 'Group portrait retry preload failed - setting to ERROR',
          onLoaded: (imageUrl) => {
            // Session may have ended during the fetch
            if (!this._isSessionValid()) {
              return updatedPortrait;
            }

            nextTick(() =>
              this.trigger(
                this.EVENTS.GROUP_GENERATED_AND_PRELOADED,
                updatedPortrait
              )
            );
            return this.update(updatedPortrait.uuid, {
              status: this.STATUSES.GENERATED_AND_PRELOADED,
              isRetrying: false,
              _originalUrl: updatedPortrait.url,
              url: imageUrl,
            });
          },
        });
      })
      .then((result) => {
        // FIX: Bail out if session ended - don't update metrics for orphaned operations
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.group_retry_orphaned',
            'Group portrait retry completed after session ended, skipping metrics',
            { uuid: sentryUuid(portrait.uuid) },
            'warning'
          );
          return result;
        }

        // Track successful retry metrics (safe - never throws)
        const duration = performance.now() - startTime;
        this._metrics.groupGenerated++;
        sentryService.incrementCounter('portrait.group_retry_success', 1, {
          style_id: portrait.styleId,
        });
        sentryService.trackMetric(
          'portrait.group_retry_duration_ms',
          duration,
          'millisecond',
          {
            style_id: portrait.styleId,
          }
        );
        sentryService.addBreadcrumb(
          'portrait.group_retry_success',
          `Group portrait retry succeeded in ${duration.toFixed(0)}ms`,
          {
            uuid: sentryUuid(portrait.uuid),
            duration_ms: duration.toFixed(0),
            retry_count: newRetryCount,
          }
        );
        this._updateSentryContext();
        return result;
      })
      .catch(async (error) => {
        // Handle intentional abort (e.g., user navigated away) - restore to ERROR state
        if (this.isAbortError(error)) {
          sentryService.addBreadcrumb(
            'portrait.group_retry_aborted',
            'Group portrait retry aborted by user',
            {
              uuid: sentryUuid(portrait.uuid),
              session_valid: this._isSessionValid(),
            }
          );

          // FIX: If session ended (reset() was called), bail out - array already cleared
          if (!this._isSessionValid()) {
            return null;
          }

          // Keep the portrait in ERROR state so user can retry again later
          return this.update(portrait.uuid, {
            status: this.STATUSES.ERROR,
            isRetrying: false,
            // Keep existing error info, don't overwrite with abort message
          });
        }

        // FIX: If session ended, skip metrics and state updates to prevent pollution
        if (!this._isSessionValid()) {
          sentryService.addBreadcrumb(
            'portrait.group_retry_orphaned_error',
            'Group portrait retry failed after session ended, skipping state update',
            {
              uuid: sentryUuid(portrait.uuid),
              error: error?.message,
            },
            'warning'
          );
          return null;
        }

        // No story takes this group any more: the tile goes.
        if (error?.groupPortraitSkipped) {
          this._removeGroupPortrait(portrait.uuid);
          return null;
        }

        console.error(
          `[GroupPortrait] Retry failed (attempt ${newRetryCount}):`,
          {
            uuid: portrait.uuid,
            faceUuids: portrait.faceUuids,
            error: errorLogSummary(error),
          }
        );

        // Track failed retry metrics (safe - never throws)
        this._metrics.groupFailed++;
        sentryService.incrementCounter('portrait.group_retry_failed', 1, {
          style_id: portrait.styleId,
        });

        // Capture error to Sentry (safe - never throws)
        // NOTE: UUIDs truncated to minimize PII exposure
        sentryService.captureException(error, {
          tags: {
            feature: 'portrait_generation',
            generation_type: 'group_retry',
            error_type: safeSentry.categorizeError(error),
            style_id: portrait.styleId,
            retry_count: String(newRetryCount),
          },
          extra: {
            session_id: sentryUuid(store.sessionId),
            portrait_uuid: sentryUuid(portrait.uuid),
            face_uuids: safeSentry.sanitizeFaceUuids(portrait.faceUuids),
            style_id: portrait.styleId,
            retry_count: newRetryCount,
            photo_count: store.photos?.length || 0,
            duration_ms: performance.now() - startTime,
          },
        });

        sentryService.addBreadcrumb(
          'portrait.group_retry_failed',
          `Group portrait retry failed: ${error?.message}`,
          {
            uuid: sentryUuid(portrait.uuid),
            error: error?.message,
            retry_count: newRetryCount,
          },
          'error'
        );
        this._updateSentryContext();

        return this.update(portrait.uuid, {
          status: this.STATUSES.ERROR,
          isRetrying: false,
          error: {
            message: error?.message || 'Failed to generate group portrait',
            details: error?.cause || error,
            timestamp: new Date().toISOString(),
            retryCount: newRetryCount,
          },
        });
      })
      .finally(() => {
        // FIX: Only update state if session is still valid
        // After reset(), groupPortraits is cleared and groupGeneratingCount is reset to 0
        if (this._isSessionValid()) {
          this.update(portrait.uuid, { isRetrying: false });
          this.groupGeneratingCount--;
        }
        // Note: If session ended, reset() already set groupGeneratingCount = 0
        // so we don't decrement to avoid going negative
      });

    this.update(portrait.uuid, { promise });
    return promise;
  }

  // The multi-subject start: the group job and the singles' batch leave
  // together, and each has its own sentinel. The promise is the one of the
  // singles: a group job can take minutes, and the step must not wait for
  // it. The group tile follows the status of its own job.
  ensureInitialPortraits(faces) {
    // Read before the group start: a group portrait takes the place of one
    // single, thus the session keeps its count of portraits.
    const groupSlots = this._initialGroupSlots(faces);
    this.ensureInitialGroupPortraits(faces).catch((error) => {
      console.warn(
        '[PortraitGeneration] Group portrait start failed:',
        errorLogSummary(error)
      );
    });
    return this.ensureInitialIndividualPortraits(faces, { groupSlots });
  }

  // The single in place of an initial group slot that shows no tile: no
  // group config took the group, or the submit got no 202. The visitor gets
  // the usual count of portraits and no message, and it is not a "+"
  // attempt. A visitor who left the step, or a newer batch, gets none.
  _makeSingleForGroupSlot(faces, controller, epoch) {
    if (
      faces.length === 0 ||
      !this._isSessionValid() ||
      controller !== this._groupGenerationController ||
      epoch !== this._groupGenerationEpoch
    ) {
      return;
    }
    const counts = faces.map(
      (face) =>
        this.portraits.filter((portrait) => portrait.faceUuid === face.uuid)
          .length
    );
    const faceIndex = counts.indexOf(Math.min(...counts));
    const single = this._queuedSingle(
      faces[faceIndex],
      this.portraits.length,
      counts[faceIndex] === 0
    );
    this.portraits = [...this.portraits, single];
    sentryService.addBreadcrumb(
      'portrait.group_slot_single',
      'A single takes the place of the group portrait',
      { face_count: faces.length }
    );
    this._updateSentryContextDebounced();
    this._launchWhenIdle({ preload: true }).catch((error) => {
      console.warn(
        '[PortraitGeneration] Single in place of the group failed:',
        errorLogSummary(error)
      );
    });
  }

  // A selection that no group config took gets no group slot.
  _initialGroupSlots(faces) {
    const styleId = store.selectedStyle?.id;
    if (!styleId || this.isGroupSelectionSkipped(faces ?? [], styleId)) {
      return 0;
    }
    return this.getGroupPortraitCount(faces?.length || 0);
  }

  // Smart generation methods that check if already generated
  async ensureInitialGroupPortraits(faces) {
    const expectedCount = this._initialGroupSlots(faces);
    if (expectedCount === 0) {
      return Promise.resolve([]);
    }

    if (this.hasGeneratedInitial('group', faces)) {
      return Promise.resolve(this.groupPortraits);
    }

    // Cancel any previous batch before starting a new one. Advance the
    // generation epoch so in-flight HTTP results from the old batch are
    // detected as stale and discarded (AbortController does not cancel
    // in-flight HTTP requests).
    if (this._groupGenerationController) {
      sentryService.addBreadcrumb(
        'portrait.cancel_for_new_batch',
        'Cancelling previous generations for new batch',
        {
          previous_controller_existed: true,
          new_face_count: faces.length,
          previous_epoch: this._groupGenerationEpoch,
        }
      );
      sentryService.incrementCounter('portrait.generations_replaced', 1);
      this._groupGenerationController.abort();
    }
    this._groupGenerationEpoch++;
    this._groupGenerationController = new AbortController();
    const controller = this._groupGenerationController;
    const epoch = this._groupGenerationEpoch;
    const signal = controller.signal;

    // Check if we already have group portraits (from previous similar session)
    const validGroupPortraits = this.groupPortraits.filter((p) => !p.isError);

    if (validGroupPortraits.length >= expectedCount) {
      this.markAsGenerated('group', faces);
      return Promise.resolve(this.groupPortraits);
    }
    const promises = [];

    for (let i = 0; i < expectedCount; i++) {
      const promise = (async () => {
        try {
          // generateGroupPortrait skips a selection that no group config takes.
          if (signal.aborted) {
            return null;
          }
          return this.generateGroupPortrait(faces);
        } catch (e) {
          if (e.name === 'AbortError') {
            return null;
          }
          throw e;
        }
      })();
      // null: the slot ended with no tile on the screen. allSettled below
      // logs a slot that failed.
      promise
        .then(
          (portrait) => {
            if (!portrait) {
              this._makeSingleForGroupSlot(faces, controller, epoch);
            }
          },
          () => undefined
        )
        .catch((error) => {
          console.warn(
            '[PortraitGeneration] Single in place of the group failed:',
            errorLogSummary(error)
          );
        });
      promises.push(promise);
    }

    this.markAsGenerated('group', faces);

    // Use Promise.allSettled to handle partial failures gracefully
    return Promise.allSettled(promises).then((results) => {
      const succeeded = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);
      const failed = results.filter((r) => r.status === 'rejected');

      if (failed.length > 0) {
        console.warn(
          `[PortraitGeneration] ${failed.length}/${results.length} group portraits failed to generate`
        );
        failed.forEach((result, index) => {
          console.error(
            `[PortraitGeneration] Group portrait ${index} error:`,
            errorLogSummary(result.reason)
          );
        });
      }

      // Return succeeded portraits (partial success is better than total failure)
      return succeeded;
    });
  }

  // `groupSlots`: the singles that the initial group portraits replace.
  async ensureInitialIndividualPortraits(faces, { groupSlots = 0 } = {}) {
    const startTime = performance.now();

    return safeSentry.withSpan(
      'portrait.ensure_initial_individual',
      'portrait.orchestration',
      async () => {
        sentryService.addBreadcrumb(
          'portrait.orchestration',
          'Starting individual portrait orchestration',
          {
            face_count: faces?.length || 0,
          }
        );

        let result;

        if (!faces || faces.length === 0) {
          result = [];
        } else if (this.hasGeneratedInitial('individual', faces)) {
          // Already generated for this session/style/faces combo
          result = this.portraits;
        } else {
          // Check if we already have sufficient portraits. A queued
          // placeholder is not a portrait the visitor has: counting it made a
          // batch that never ran look complete, and the step returned without
          // starting the singles again.
          const expectedCount =
            this.getPortraitCountPerFace(faces.length) * faces.length -
            groupSlots;
          const validPortraits = this.portraits.filter(
            (p) => !(p.isError || p.isNeverGenerated)
          );

          if (validPortraits.length >= expectedCount) {
            this.markAsGenerated('individual', faces);
            result = this.portraits;
          } else {
            // Only add to queue if we don't have enough portraits
            if (this.portraits.length < expectedCount) {
              this.addToQueue(faces, { groupSlots });
            }

            // Mark after the batch ran, not before it. A launch that throws
            // must leave the state unmarked, or the next entry to the step
            // reads "already generated" and the singles never start again.
            // launch() carries its own isGenerating guard, so a second caller
            // during this launch still starts no duplicate batch.
            result = await this.launch({ preload: true });
            this.markAsGenerated('individual', faces);
          }
        }

        // Track orchestration duration
        const duration = performance.now() - startTime;
        sentryService.trackMetric(
          'portrait.orchestration_duration_ms',
          duration,
          'millisecond',
          {
            type: 'individual',
            face_count: String(faces?.length || 0),
          }
        );

        return result;
      },
      {
        'orchestration.type': 'individual',
        'orchestration.face_count': faces?.length || 0,
      }
    );
  }

  /**
   * Cancels any pending staggered group generation requests.
   * Call this when navigating away from portrait selection (e.g., to payment/printing).
   */
  cancelPendingGenerations() {
    if (this._groupGenerationController) {
      sentryService.addBreadcrumb(
        'portrait.cancel',
        'User cancelled pending portrait generations',
        {
          had_active_controller: true,
          pending_group_portraits: this.groupPortraits.filter(
            (p) => p.isGenerating
          ).length,
          group_generating_count: this.groupGeneratingCount,
        }
      );
      sentryService.incrementCounter('portrait.generations_cancelled', 1);

      this._groupGenerationController.abort();
      this._groupGenerationController = null;
    }
  }

  reset() {
    // CRITICAL: Nullify generation session ID FIRST to prevent orphaned promise handlers
    // from updating state after this point. In-flight generations compare this against
    // store.sessionId and bail out when they no longer match.
    this._generationSessionId = null;

    // Cancel any pending staggered group generation timeouts
    this.cancelPendingGenerations();

    // Cancel pending debounced Sentry context updates
    this._updateSentryContextDebounced.cancel();

    // Sentry breadcrumb for reset with final metrics (safe - never throws)
    sentryService.addBreadcrumb(
      'portrait.reset',
      'Portrait generation state reset',
      {
        portraits_cleared: this.portraits.length,
        group_portraits_cleared: this.groupPortraits.length,
        was_generating: this.isGenerating,
        group_generating_count: this.groupGeneratingCount,
        final_metrics: {
          total_generated: this._metrics.totalGenerated,
          total_failed: this._metrics.totalFailed,
          total_retries: this._metrics.totalRetries,
          group_generated: this._metrics.groupGenerated,
          group_failed: this._metrics.groupFailed,
        },
      }
    );

    // Drop queued reveals first: they close over the outgoing session's
    // portraits, and their timer would otherwise outlive the session.
    this._revealPacer.cancel();

    // Clean up blob URLs before clearing to prevent memory leaks
    for (const portrait of [...this.portraits, ...this.groupPortraits]) {
      if (portrait.url?.startsWith('blob:')) {
        URL.revokeObjectURL(portrait.url);
      }
    }

    this.portraits = [];
    this.groupPortraits = [];
    this.attemptsCount = 0;
    this._skippedGroupSelections.clear();
    this.generationState.clear(); // Clear generation state tracking

    // Explicitly reset generation state flags to prevent stuck state.
    // Promise handlers that normally clear these may not run, or may run
    // after reset() completes, so reset() must clear them itself.
    this.isGenerating = false;
    this._activeLaunch = null;
    this.groupGeneratingCount = 0;

    // Advance epoch so any in-flight group generation results from the old session
    // are detected as stale and discarded when their .then() handlers execute.
    this._groupGenerationEpoch++;

    // Reset metrics for new session
    this._metrics = {
      totalGenerated: 0,
      totalFailed: 0,
      totalRetries: 0,
      groupGenerated: 0,
      groupFailed: 0,
    };

    printing.reset(); // Clear printing queue when portraits are reset
    this.resetPubSub();

    // Clear Sentry context for portrait generation
    sentryService.setContext('portrait_generation', null);
  }
}

export const portraitGeneration = new PortraitGeneration();
